import React, { useRef, memo, useMemo, useState, useEffect } from 'react';
import { Box, Typography, TextField } from '@availity/element';
import searchIcon from '../../../static/assets/icons/magnifying-glass.svg';
import angleDownIcon from '../../../static/assets/icons/angle-down.svg';
import angleRightIcon from '../../../static/assets/icons/angle-right.svg';
import { iconFilters, themeColors } from '../../../static/constants/styles';
import useSearch from '../../../hooks/useSearch';
import useScrollSync from '../../../hooks/useScrollSync';
import useSearchShortcut from '../../../hooks/useSearchShortcut';

export interface SidebarSection {
  /** Key used to identify this collapsible section */
  key: string;
  /** Label shown on the collapsible section button */
  label: string;
  /** Nav items inside this section */
  items: { id: string; label: string }[];
}

interface PageSidebarProps {
  sections?: SidebarSection[];
  expandedSection?: Set<string> | string | null;
  activeSection?: string | null;
  activeContent?: string;
  onToggleSection?: (section: string) => void;
  onMenuItemClick?: (contentId: string) => void;
  onScrollSync?: (contentId: string) => void;
  theme: 'light' | 'dark';
  children?: React.ReactNode;
  searchItems?: { id: string; label: string }[];
  onSearchResultClick?: (id: string) => void;
  isScrollingRef?: React.RefObject<boolean>;
}

const isExpanded = (expandedSection: Set<string> | string | null, section: string) =>
  expandedSection instanceof Set ? expandedSection.has(section) : expandedSection === section;

const SidebarButton = memo(({ section, label, expandedSection, activeSection, onToggleSection, theme }: {
  section: string; label: string; expandedSection: Set<string> | string | null; activeSection: string | null;
  onToggleSection: (s: string) => void; theme: 'light' | 'dark';
}) => (
  <Box
    component="button"
    onClick={() => onToggleSection(section)}
    sx={{
      p: '10px 12px',
      bgcolor: (activeSection ? activeSection === section : isExpanded(expandedSection, section)) ? themeColors[theme].sidebarButton : 'transparent',
      borderRadius: '6px', fontSize: '14px', mb: 0.5,
      display: 'flex', alignItems: 'center', gap: 1,
      border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer',
      '&:hover': { bgcolor: themeColors[theme].sidebarButton }
    }}
  >
    <Box component="img"
      src={isExpanded(expandedSection, section) ? angleDownIcon : angleRightIcon}
      sx={{ width: 16, height: 16, filter: theme === 'light' ? iconFilters.dark : iconFilters.light }}
    />
    <Typography sx={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: themeColors[theme].text }}>
      {label}
    </Typography>
  </Box>
));

const SidebarMenuItem = memo(({ label, contentId, activeContent, onMenuItemClick, theme }: {
  label: string; contentId: string; activeContent: string;
  onMenuItemClick: (id: string) => void; theme: 'light' | 'dark';
}) => (
  <Typography
    onClick={() => {
      onMenuItemClick(contentId);
    }}
    sx={{
      width: '100%', maxWidth: 249, height: 36, p: '8px 16px',
      color: activeContent === contentId ? themeColors[theme].text : `${themeColors[theme].text}E5`,
      bgcolor: activeContent === contentId ? themeColors[theme].sidebarButtonHover : 'transparent',
      fontSize: '16px', fontFamily: 'Lato', fontWeight: 400, lineHeight: '24px',
      letterSpacing: '0.01em', cursor: 'pointer', borderRadius: '4px',
      display: 'flex', alignItems: 'center',
      '&:hover': { bgcolor: themeColors[theme].sidebarButtonHover, color: themeColors[theme].text }
    }}
  >
    {label}
  </Typography>
));

// `noop` is stable — defined outside component to avoid re-creation
const noop = () => { /* noop */ };

const PageSidebar = ({ sections, expandedSection, activeSection, activeContent, onToggleSection, onMenuItemClick, onScrollSync, theme, children, searchItems, onSearchResultClick, isScrollingRef }: PageSidebarProps): JSX.Element => {
  const [isMac, setIsMac] = useState(true);
  useEffect(() => { setIsMac(navigator.platform?.toUpperCase().includes('MAC') ?? /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)); }, []);
  const allItems = useMemo(() => searchItems ?? sections?.flatMap((s) => s.items) ?? [], [searchItems, sections]);
  const scrollItems = useMemo(() => sections?.flatMap((s) => s.items) ?? [], [sections]);
  const handleClick = useMemo(
    () => onSearchResultClick ?? onMenuItemClick ?? noop,
    [onSearchResultClick, onMenuItemClick]
  );
  const { searchQuery, searchResults, handleSearch, handleSearchClick } = useSearch(handleClick, allItems);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useScrollSync(scrollItems, onScrollSync ?? onMenuItemClick ?? noop, isScrollingRef);
  useSearchShortcut(searchInputRef);

  return (
    <Box sx={{ width: 280, bgcolor: themeColors[theme].background, p: '24px 16px', borderRight: `1px solid ${themeColors[theme].border}`, position: 'sticky', top: 0, alignSelf: 'flex-start', height: '100vh', overflowY: 'auto', overflowX: 'hidden' }}>
      {/* Search */}
      <Box sx={{ mb: 2, position: 'relative' }}>
        <TextField
          fullWidth
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <Box component="img" src={searchIcon} sx={{ width: 12, height: 12, mr: 1, filter: theme === 'light' ? iconFilters.dark : iconFilters.white }} />
            ),
            endAdornment: !searchQuery && (
              <Box sx={{ borderRadius: '8px', padding: '2px 8px', bgcolor: themeColors[theme].cardBackground, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography sx={{ fontFamily: 'Lato', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: themeColors[theme].text }}>{isMac ? '⌘' : '^'}</Typography>
                <Typography sx={{ fontFamily: 'Lato', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em', color: themeColors[theme].text }}>K</Typography>
              </Box>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: 36, borderRadius: '4px', border: `1px solid ${themeColors[theme].border}`,
              bgcolor: themeColors[theme].background, justifyContent: 'space-between',
              opacity: 1, paddingRight: '4px', paddingTop: '4px', paddingBottom: '4px',
              '& input': { padding: '6px 4px 6px 8px', fontFamily: 'Lato', fontWeight: 400, fontSize: '16px', color: themeColors[theme].text, textAlign: 'left' }
            },
            '& .MuiFormHelperText-root': { display: 'none' }
          }}
        />
        {searchQuery && searchResults.length > 0 && (
          <Box sx={{ position: 'absolute', top: '100%', left: 0, right: 0, bgcolor: themeColors[theme].cardBackground, border: `1px solid ${themeColors[theme].border}`, borderRadius: '4px', mt: 1, maxHeight: 300, overflowY: 'auto', zIndex: 10 }}>
            {searchResults.map((item) => (
              <Box key={`${item.id}-${item.matchIndex}`} onClick={() => handleSearchClick(item.id, item.matchIndex)}
                sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: themeColors[theme].sidebarButton }, color: themeColors[theme].text, borderBottom: `1px solid ${themeColors[theme].border}` }}
              >
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: '14px' }}>{item.label}</Typography>
                <Typography sx={{ fontSize: '12px', color: themeColors[theme].textMuted }}>{item.snippet}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Sections — static mode */}
      {sections?.map((section) => (
        <Box key={section.key}>
          <SidebarButton section={section.key} label={section.label} expandedSection={expandedSection ?? null} activeSection={activeSection ?? null} onToggleSection={onToggleSection ?? noop} theme={theme} />
          {isExpanded(expandedSection ?? null, section.key) && (
            <Box sx={{ pl: 4 }}>
              {section.items.map((item) => (
                <SidebarMenuItem key={item.id} label={item.label} contentId={item.id} activeContent={activeContent ?? ''} onMenuItemClick={onMenuItemClick ?? noop} theme={theme} />
              ))}
            </Box>
          )}
        </Box>
      ))}

      {/* Children — dynamic mode (e.g. GuidesTab) */}
      {children}
    </Box>
  );
};

export default PageSidebar;
